import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/aevos/PageHero";
import { Activity, FlaskConical, Sparkles, CheckCircle2, Plus, Minus } from "lucide-react";

const techs = [
  {
    Icon: Activity,
    name: "Bioimpedância por Ultrassom — BodyMetrix",
    summary:
      "O BodyMetrix é um aparelho de ultrassom portátil que avalia a composição corporal de forma precisa e sem estimativas. Ele gera imagens em tempo real que mostram a espessura exata (em milímetros) da gordura subcutânea e dos músculos, além de calcular o percentual de gordura total e massa magra.",
    use: "Avaliações iniciais e reavaliações periódicas em todos os planos.",
    how: {
      lead: "Como funciona",
      blocks: [
        {
          title: "Tecnologia de Imagem",
          text: "Através da emissão de ondas de ultrassom, o equipamento identifica os tecidos (gordura, músculo e osso) com base na densidade de cada um.",
        },
        {
          title: "Exatidão",
          text: "Diferente da bioimpedância tradicional, o método não sofre alterações com o nível de hidratação do paciente, consumo de cafeína ou ciclo menstrual.",
        },
        {
          title: "Escaneamento",
          text: "O profissional utiliza um pequeno transdutor com gel sobre pontos específicos do corpo para escanear e gerar um relatório completo no computador.",
        },
      ],
      diff_title: "Principais diferenciais",
      diffs: [
        "Visualização Real — o paciente vê na tela as camadas de gordura e músculo, acompanhando visualmente a perda de gordura e o ganho de massa magra.",
        "Avaliação da Qualidade Muscular — permite identificar a gordura infiltrada no tecido muscular.",
        "Acompanhamento pós-cirúrgico e lesões — ideal para fisioterapeutas medirem simetrias e atrofias musculares comparando os membros (ex.: perna direita vs. esquerda).",
        "Variedade de protocolos — aceita diversos protocolos antropométricos, como o de dobras cutâneas (Jackson e Pollock), para conversão dos dados em métricas de saúde.",
      ],
    },
  },
  {
    Icon: FlaskConical,
    name: "Calorimetria Indireta",
    summary:
      "A calorimetria indireta é um exame não invasivo que mede com precisão o seu gasto calórico diário em repouso e identifica se o metabolismo utiliza mais gorduras ou carboidratos para gerar energia. O procedimento fornece dados essenciais para o ajuste fino de dietas.",
    use: "Incluída a partir do AEVOS Performance.",
    how: {
      lead: "Como funciona",
      blocks: [
        {
          title: "Avaliação respiratória",
          text: "O teste avalia a respiração através de um equipamento chamado carrinho metabólico.",
        },
        {
          title: "Medição",
          text: "Uma máscara facial ou capuz transparente é colocado sobre o rosto. O aparelho capta todo o volume do ar expirado.",
        },
        {
          title: "Trocas gasosas",
          text: "Ele mede exatamente quanto oxigênio (O₂) você consome e quanto gás carbônico (CO₂) você produz.",
        },
        {
          title: "Cálculo",
          text: "A partir dessa proporção, determina-se a Taxa Metabólica de Repouso (TMR) em quilocalorias e o Quociente Respiratório (QR), indicando a sua queima de substratos (gordura vs. carboidrato).",
        },
      ],
      diff_title: "Observação importante",
      diffs: ["Para obter resultados exatos, é necessário preparo prévio orientado pela equipe médica."],
    },
  },
  {
    Icon: Sparkles,
    name: "Protocolo AEVOS",
    summary:
      "Protocolos terapêuticos personalizados para saúde, performance e longevidade. Na AEVOS, acreditamos que cada organismo possui necessidades únicas — por isso, os protocolos são definidos exclusivamente após avaliação médica, respeitando critérios clínicos, exames laboratoriais e os objetivos individuais de cada paciente.",
    use: "As terapias podem ser utilizadas como complemento dos programas de acompanhamento, potencializando resultados relacionados à saúde metabólica, energia, composição corporal, recuperação e longevidade.",
    how: {
      lead: "Como funciona",
      blocks: [
        { title: "Suporte metabólico", text: "Estratégias clínicas individualizadas para otimização metabólica." },
        { title: "Saúde e performance física", text: "Protocolos voltados ao rendimento físico sustentável." },
        { title: "Energia e disposição", text: "Apoio à melhora da vitalidade e do desempenho diário." },
        { title: "Recuperação muscular", text: "Suporte complementar à recuperação tecidual." },
        { title: "Longevidade saudável", text: "Estratégias para envelhecimento ativo e qualidade de vida." },
        { title: "Saúde hormonal", text: "Acompanhamento hormonal sempre mediante avaliação médica." },
        { title: "Qualidade de vida", text: "Foco em bem-estar contínuo e vitalidade." },
      ],
      diff_title: "Protocolos disponíveis",
      diffs: [
        "Metabolic Boost — otimização metabólica, emagrecimento e eficiência energética.",
        "Metabolic Recovery — suporte complementar para resistência insulínica, síndrome metabólica e acompanhamento de peso.",
        "Performance Energy — disposição física e mental, recuperação e rendimento diário.",
        "Performance Muscle — preservação e desenvolvimento de massa muscular.",
        "Longevity Prime — envelhecimento saudável, vitalidade e qualidade de vida ao longo do tempo.",
        "Beauty Recovery — saúde da pele, cabelos e unhas.",
        "Hormonal Care — protocolos individualizados de acompanhamento hormonal, sempre mediante avaliação médica.",
      ],
    },
  },
];

const TechCard = ({ t, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="aevos-card p-6 md:p-10 grid md:grid-cols-5 gap-8" data-testid={`tech-${i}`}>
      <div className="md:col-span-1 flex md:flex-col gap-4 items-start">
        <div className="w-14 h-14 border border-black flex items-center justify-center">
          <t.Icon size={24} className="text-black" />
        </div>
        <p className="aevos-eyebrow">Tecnologia {String(i + 1).padStart(2, "0")}</p>
      </div>
      <div className="md:col-span-4">
        <h3 className="font-display font-bold text-xl md:text-2xl text-black">{t.name}</h3>
        <p className="mt-2 text-[#333] leading-relaxed">{t.summary}</p>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-6 w-full md:w-auto inline-flex items-center justify-between gap-3 px-4 py-3 border border-black hover:bg-[#00C4FF] hover:border-[#00C4FF] transition-colors font-display font-semibold text-sm text-black"
          data-testid={`tech-${i}-toggle`}
          aria-expanded={open}
        >
          <span>Como funciona</span>
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </button>

        {open && (
          <div className="mt-6 border-l-2 border-[#00C4FF] pl-5 space-y-4" data-testid={`tech-${i}-content`}>
            {t.how.blocks.map((b) => (
              <div key={b.title}>
                <p className="font-display font-semibold text-sm text-black">{b.title}</p>
                <p className="text-sm text-[#333] mt-1 leading-relaxed">{b.text}</p>
              </div>
            ))}

            {t.how.diffs?.length > 0 && (
              <div className="pt-4">
                <p className="aevos-eyebrow">{t.how.diff_title}</p>
                <ul className="mt-3 space-y-2">
                  {t.how.diffs.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-[#333]">
                      <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[#E0E0E0]">
          <p className="text-xs text-[#666] uppercase tracking-wider font-semibold">Uso clínico</p>
          <p className="text-sm text-[#333] mt-1">{t.use}</p>
        </div>
      </div>
    </div>
  );
};

const Tecnologia = () => {
  return (
    <div data-testid="page-tecnologia">
      <PageHero
        eyebrow="Tecnologia & Exames"
        title="Decisões clínicas baseadas em dados reais, não em estimativas."
        subtitle="A AEVOS combina exames laboratoriais com tecnologia de precisão diagnóstica para mapear, com objetividade, a biologia única de cada paciente."
      />

      <section className="section-pad">
        <div className="aevos-container space-y-6">
          {techs.map((t, i) => (
            <TechCard key={t.name} t={t} i={i} />
          ))}

          <div className="p-6 md:p-8 border border-[#E0E0E0] bg-[#E6F8FF] text-sm text-[#333]">
            <p className="aevos-eyebrow mb-2">Importante</p>
            <p>
              Os protocolos terapêuticos não são comercializados de forma isolada e não substituem
              acompanhamento médico. Sua indicação é sempre individualizada, baseada em critérios
              clínicos, laboratoriais e nos objetivos específicos de cada paciente. Investimento
              conforme avaliação médica.
            </p>
          </div>
        </div>
      </section>

      <section className="pt-20 md:pt-28 pb-10 md:pb-14 bg-black text-white text-center">
        <div className="aevos-container">
          <h2 className="font-display font-bold text-3xl md:text-5xl max-w-3xl mx-auto leading-[1.1] !text-white">
            Quer entender qual conjunto de exames faz sentido para <span className="text-[#00C4FF]">você</span>?
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contato"><span className="btn-cyan">Agendar Consulta</span></Link>
            <Link to="/mapeamento"><span className="btn-ghost-on-dark">Fazer Mapeamento Online</span></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tecnologia;
